import { useTranslation } from 'react-i18next';
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import cx from 'clsx';

import { useDeviceTypeIsMobile, useQueryParams, useMergedConfig } from '@/hooks';
import { IconExitAnalysis } from '@/assets/history';
import { Downgrade } from '@/components';

import { List as TabHistory } from '../../';
import { AnalysisModeButton } from '../AnalysisModeButton';
import { HistoryAnalysisView } from '../HistoryAnalysisView';
import style from './index.module.css';
import { ContentScope } from '../../types';

export type HistoryContainerProps = {
  address: string;
  scope: ContentScope;
  onAnalysisChange?(arg: 'history' | 'analysis' | undefined): void;
};

export const HistoryContainer = ({ address, scope, onAnalysisChange }: HistoryContainerProps) => {
  const { t } = useTranslation();
  const { data: bundleConfig, isLoading: configLoading } = useMergedConfig(address);

  const isMobile = useDeviceTypeIsMobile();

  const [queryMode, setQueryMode] = useQueryParams<'history' | 'analysis' | undefined>('mode');
  const [mode, setMode] = useState<'history' | 'analysis'>(queryMode || 'history');

  const isAnalysisMode = useMemo(() => mode === 'analysis', [mode]);
  const isHistoryMode = useMemo(() => !isAnalysisMode, [isAnalysisMode]);

  useEffect(() => {
    if (!queryMode && mode === 'history') {
      return;
    }
    setQueryMode(mode === 'analysis' ? 'analysis' : undefined);
  }, [mode, queryMode, setQueryMode]);

  const goBackHistory = useCallback(() => {
    setMode('history');
  }, [setMode]);

  useEffect(() => {
    onAnalysisChange?.(mode);
  }, [mode, onAnalysisChange]);

  useEffect(() => {
    if (isMobile) {
      goBackHistory();
    }
  }, [goBackHistory, isMobile]);

  return configLoading ? null : isMobile || bundleConfig?.history?.can_load ? (
    <div className={style.historyContainer}>
      {isHistoryMode && !isMobile ? (
        <AnalysisModeButton
          className={style.analysisModeButton}
          address={address}
          onEnter={() => setMode('analysis')}
        />
      ) : null}

      {isAnalysisMode ? (
        <div className={style.analysisModeHeader}>
          <span className={style.title}>{t('HistoryContainer.13hiqnb')}</span>
          <div className={style.backButton} onClick={() => setMode('history')}>
            <IconExitAnalysis />
            {t('HistoryContainer.18uughx')}
          </div>
        </div>
      ) : null}

      <TabHistory
        className={cx(!isHistoryMode && style.invisibleView)}
        id={address}
        scope={scope}
      />
      {isAnalysisMode ? <HistoryAnalysisView address={address} goBack={goBackHistory} /> : null}
    </div>
  ) : (
    <Downgrade />
  );
};
